import { routeGuard } from '../config/route-guards';
import { getScopeRole } from '@/services/current-context';
import {
  fetchCustomerOrgListRequest,
  fetchInternalOrgPickerRequest,
} from './entity-scope-api';
import { switchEntity, type SwitchPropagation } from './entity-scope';
import { isSessionEnding } from './switch-errors';
import { endSession } from './auth-token';
import { getEntityScope } from './entity-scope-read';
import { resolveViaAncestorWalk } from './entity-scope-ancestor-walk';
import { denied, storedTargetUnlessRefused } from './entity-scope-denial';
import { READY } from './entity-scope-outcome';
import { storeIntendedUrl } from './intended-navigation';
import { getAllOrganisation, initiateOrgSwitch } from './org-data';
import { toRoleSuffix } from './entity-resolution';
import { isInternalRole } from '../util/roles';
import type { ScopeOutcome } from './entity-scope-outcome';
import type { AccessibleOrg } from '../types/org-selection.types';

/**
 * The auth bootstrap's entity-scope step (DV-3012 §3.3, DV-3013 §3.3).
 *
 * Decomposed in DV-3013 PR 4 (spec §4): the ancestor walk, the denial path and
 * the outcome union now live in `entity-scope-ancestor-walk.ts`,
 * `entity-scope-denial.ts` and `entity-scope-outcome.ts`. What is left is the
 * org list and the step itself.
 *
 * `ScopeOutcome` is re-exported so callers keep importing it from here — it is
 * this module's contract, not the leaf file's.
 */
export type { ScopeOutcome } from './entity-scope-outcome';

/**
 * An org list plus its provenance. The caller needs `fromCache` to decide
 * whether a failed lookup is worth retrying: a list that already came off the
 * wire cannot be made fresher by asking again.
 */
interface OrgList {
  orgs: AccessibleOrg[];
  fromCache: boolean;
}

/**
 * The accounts the active user can scope into.
 *
 * Cache first, then a role-branched fetch — the policy `EntityScopeGuard`
 * had. Owning the freshness decision here is why `OrganizationSelectionPage`
 * no longer sniffs `performance.getEntriesByType('navigation')` to guess
 * whether its cache is trustworthy (spec §4.1).
 *
 * Note what that does NOT amount to: this runs only on the target-resolution
 * path and the scope-required tail, so it writes nothing for an
 * `entity: 'none'` route like `/select-organization` — and its caller's catch
 * assigns `initiateOrgSwitch()` (which returns `unscopedLandingRoute()` after clearing
 * `selectedOrg` and the stale `entityTree`) even when the fetch above threw.
 * The picker keeps a narrow refetch of its own for exactly that case; it is
 * not redundant.
 *
 * The cached payload goes through `getAllOrganisation()`, which owns the
 * per-row filter: a session that has not logged back in since the `OrgData`
 * normalisation shipped still holds pre-normalisation rows, and trusting one
 * would resolve a target to a row with no name — an unresolvable match
 * rather than the cache miss it actually is. No such row survives the
 * filter, and a payload of nothing but those reads as the miss it is.
 */
async function accessibleOrgs(
  { refresh }: { refresh: boolean } = { refresh: false },
): Promise<OrgList> {
  if (!refresh) {
    const cached = getAllOrganisation();
    if (cached.length > 0) return { orgs: cached, fromCache: true };
  }

  const userId = localStorage.getItem('userId') ?? '';
  const role = getScopeRole();
  // Both internal roles read the internal picker (DV-3337): entity-service
  // admits any cross-account internal caller, and it is the projected read
  // DV-2958 built for login.
  const orgs = isInternalRole(role)
    ? await fetchInternalOrgPickerRequest(toRoleSuffix(role))
    : await fetchCustomerOrgListRequest(userId);
  localStorage.setItem('OrgData', JSON.stringify(orgs));
  return { orgs, fromCache: false };
}

/**
 * Resolve the entity scope this route needs, before anything renders
 * (DV-3012, spec §3.3).
 *
 * Intended for the two auth-bootstrap entry points — `bootstrapAuth` for a
 * returning session, `CallBack` after the token exchange — because
 * `bootstrapAuth` alone cannot cover the fresh login: `/callback` loads with no
 * token, so it bails before `CallBack` has one (spec §3.1 / D1). `Loader`
 * reaches it through the same hook `CallBack` does (`useScopeBootstrap`).
 *
 * This function OWNS navigation; the caller owns rendering (spec §3.4). Every
 * outcome but `ready` and `no-organizations` has already started a trip by the
 * time it is returned — a switch's assign, the picker route, or `/error`.
 */
export async function resolveEntityScope(path: string): Promise<ScopeOutcome> {
  const guard = routeGuard(path);
  // Routes declared `entity: 'none'` resolve nothing. Per the current guard
  // table that is `/register`, `/additional-details`, `/select-organization`,
  // `/internal/*`, `/account/*` (DV-3520's redirect to `/internal`) and
  // `/error` — the registration steps, the pre-selection surfaces, the old
  // console prefix, and the terminal error surface. The pickers among them ARE
  // where an unscoped user lands, so re-resolving a deep link here would deny
  // the user again on the very page whose job is to let them pick a reachable
  // account instead (BC-2). `/error` resolving would be worse: it would deny
  // again and reassign `/error?reason=...`, looping.
  if (guard.entity === 'none') return READY;

  const fromUrl = new URLSearchParams(window.location.search).get('entityUUID');
  const fromStore = storedTargetUnlessRefused();
  // The current URL wins: a stored intendedUrl is older by construction.
  const target = fromUrl ?? fromStore;
  // Compared against the SLICE, not the token claim (DV-3573 review). A switch
  // no longer moves the token, so the claim names the login account while this
  // tab may be looking at another; comparing against it would mount an
  // `?entityUUID=A` link scoped to whatever the tab last showed, and re-run a
  // full switch on every load of a link to the account already shown.
  //
  // TWO terms, and the second is not redundant: the entity id survives
  // `scopeCleared`'s un-choose, so "the target already equals the selection"
  // does NOT mean "already scoped". An admin on `/internal/*` (entity: 'none',
  // therefore unscoped) who follows Manage Assets into an order whose entity
  // happens to be their last-used one would otherwise resolve nothing here and
  // be sent to the picker — losing the target, which is the exact bounce this
  // ticket exists to remove, on its headline flow. Resolving instead runs
  // `switchEntity`, which scopes the session and lands them.
  const scope = getEntityScope();
  const targetToResolve =
    target && (target !== scope.entityId || scope.status !== 'scoped')
      ? target
      : null;

  // Local state was wiped (logout in another tab) while this route was still
  // resolving. Checked ahead of BOTH work paths, not just the target one:
  // without a userId there is nothing to resolve a target against, and the
  // org-list call for the scope-required tail would go out with an empty user
  // path segment. An `entity: 'any'` route with nothing to resolve is
  // deliberately exempt — it mounts either way, and `/callback` reaches here
  // mid-login, before the token exchange has written `userId`.
  if (
    (targetToResolve || guard.entity === 'required') &&
    !localStorage.getItem('userId')
  ) {
    endSession();
    return { kind: 'navigating' };
  }

  if (targetToResolve) {
    // Provenance decides propagation (D5): a target on the current URL has
    // nowhere to navigate to; one from a stored intendedUrl must be landed on.
    // The intended URL is only PEEKED here — `switchEntity`'s 'land-intended'
    // mode consumes it, so a denial leaves it stored (BC-2).
    const propagate: SwitchPropagation = fromUrl ? 'stay' : 'land-intended';
    try {
      const first = await accessibleOrgs();
      let resolved = await resolveViaAncestorWalk(targetToResolve, first.orgs);
      // One retry, and only against a CACHED first answer: access may have
      // been granted since the cache was written. A first answer that already
      // came off the wire is as fresh as a refetch would be, so retrying it
      // would just pay for the same list and the same walk twice on every
      // genuine denial.
      if (!resolved && first.fromCache) {
        const refreshed = await accessibleOrgs({ refresh: true });
        resolved = await resolveViaAncestorWalk(targetToResolve, refreshed.orgs);
      }
      if (!resolved) {
        return denied('entity-access', targetToResolve, { fromStore: !fromUrl });
      }
      if (!resolved.entityName || !resolved.role) {
        // An empty name would persist selectedOrg='' and break every
        // selectedOrg-interpolated URL (PR #1131 review, finding 6).
        return denied('unknown', targetToResolve, {
          fromStore: !fromUrl,
          error: 'unresolvable match: no name or role',
        });
      }

      await switchEntity(resolved, propagate);
      return propagate === 'stay' ? { kind: 'switching' } : { kind: 'navigating' };
    } catch (error) {
      // The switch ended the session and its navigation is pending. Navigating
      // to /error here could unload the page before the logout's cleanup runs,
      // and would count as an access denial (DV-3421 review round 5, item 4).
      if (isSessionEnding(error)) return { kind: 'navigating' };
      console.error('Entity scope resolution failed:', error);
      return denied('unknown', targetToResolve, { fromStore: !fromUrl, error });
    }
  }

  // Nothing to resolve, and the route mounts with or without a scope.
  if (guard.entity === 'any') return READY;

  // The route needs an entity, and the slice answers both halves (DV-3573: the
  // token claim no longer says which account this tab is in): `status` says
  // one was CHOSEN in this tab — `initiateOrgSwitch()` clears it, and that has
  // to keep forcing the picker — and `entityId` says which. Both entry points
  // hydrate the slice before calling this (`bootstrapAuth` directly,
  // `useScopeBootstrap` through its own dispatch), so the read is never ahead
  // of the store.
  if (scope.status === 'scoped' && scope.entityId) return READY;

  // Every exit from here navigates away from the route the user asked for —
  // the single-org auto-select assigns, the picker branch assigns, and so
  // does the catch. Capture the target first or it is simply lost: none of
  // those three paths can reconstruct it, and `'land-intended'` falls back
  // to `LoginConstants.NAVIGATE_TO` when nothing is stored.
  //
  // `PrivateRoute` used to own this line (it still does for the cases it
  // still sees), but the bootstrap now pre-empts render, so on a full-page
  // load of a deep link `PrivateRoute` never runs and the capture has to
  // happen here. Safe from the hook's entry point too: `storeIntendedUrl`
  // only writes `/home` paths, so from '/' or '/callback' it is a no-op and
  // cannot overwrite the link those surfaces are mid-way through landing.
  storeIntendedUrl();

  try {
    const orgs = (await accessibleOrgs()).orgs.filter((org) => !org.disabled);
    if (orgs.length === 0) return { kind: 'no-organizations' };
    if (orgs.length === 1) {
      const only = orgs[0];
      await switchEntity(
        {
          entityId: only.entityId,
          entityName: only.name,
          role: only.role,
          parentEntityId: only.entityId,
        },
        'land-intended',
      );
      return { kind: 'navigating' };
    }
    // Still a document load, deliberately, and it is now a MIXED regime: §3.5's
    // correction establishes that `Loader` and `CallBack` reach this function
    // through `useScopeBootstrap` inside the router, where the port IS live, so
    // from those two surfaces a successful SWITCH lands via the router while
    // this bounce does not (PR #1150 review, finding 6). Left alone here
    // because the picker bounce is the one path where a clean document is worth
    // having — it is reached when the scope could not be established at all,
    // and `bootstrapAuth`'s pre-render call site has no router to use anyway.
    // Considered routing this through the port in PR 4; kept the document
    // load instead, for the reason stated just above — this is the one path
    // where a clean document is worth having, not a gap left for later.
    //
    // `initiateOrgSwitch()`, not `unscopedLandingRoute()`: it clears `selectedOrg` (and
    // the stale `entityTree`) as well as returning the route. Reaching here
    // means the claim is falsy while `selectedOrg` may well still be set —
    // and `PrivateRoute` reads the picker's `entity: 'none'` as the assertion
    // "no org is selected", so assigning the route without clearing bounces
    // the picker straight back to `/`, into `Loader`, into this same branch:
    // a reload loop at one full document load per lap. Clearing is also what
    // "you need to pick an org" actually means.
    window.location.assign(initiateOrgSwitch());
    return { kind: 'navigating' };
  } catch (error) {
    // Same as above: the single-org auto-select's switch ended the session.
    if (isSessionEnding(error)) return { kind: 'navigating' };
    console.error('Entity scope: could not load the org list:', error);
    // Not a denial — the user's access is unknown, not refused. The picker can
    // retry on its own mount. Same clearing as the branch above, same reason.
    window.location.assign(initiateOrgSwitch());
    return { kind: 'navigating' };
  }
}
